import React, { useState, useEffect } from 'react';
import { X, Save, Plus } from 'lucide-react';
import { Feedback, FeedbackCategory, FeedbackPriority } from '../types';
import { Button, Input, Select } from './ui';
import { useUser } from '../contexts/UserContext';

interface FeedbackFormProps {
  hotelId: string;
  hotelName: string;
  feedback?: Feedback | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedbackData: Omit<Feedback, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
}

const categoryOptions = [
  { value: 'bug-report', label: 'Bug Report' },
  { value: 'feature-request', label: 'Feature Request' },
  { value: 'ui-ux-issue', label: 'UI/UX Issue' },
  { value: 'general-feedback', label: 'General Feedback' },
];

const priorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

export function FeedbackForm({
  hotelId,
  hotelName,
  feedback,
  isOpen,
  onClose,
  onSubmit,
}: FeedbackFormProps) {
  const { user } = useUser();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general-feedback' as FeedbackCategory,
    priority: 'medium' as FeedbackPriority,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!feedback;

  useEffect(() => {
    if (feedback) {
      setFormData({
        title: feedback.title,
        description: feedback.description,
        category: feedback.category,
        priority: feedback.priority,
      });
    } else {
      setFormData({
        title: '',
        description: '',
        category: 'general-feedback',
        priority: 'medium',
      });
    }
    setErrors({});
  }, [feedback, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !user) return;

    setIsSubmitting(true);
    try {
      const feedbackData: Omit<Feedback, 'id' | 'createdAt' | 'updatedAt'> = {
        hotelId,
        hotelName,
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        priority: formData.priority,
        status: feedback?.status || 'open',
        submittedBy: user.id,
        submittedByEmail: user.email,
        submittedByName: user.displayName,
        comments: feedback?.comments || [],
        isReadByAdmin: false,
        // Only include optional fields if they have values
        ...(feedback?.resolvedAt && { resolvedAt: feedback.resolvedAt }),
        ...(feedback?.resolvedBy && { resolvedBy: feedback.resolvedBy }),
        ...(feedback?.resolvedByName && { resolvedByName: feedback.resolvedByName }),
        ...(feedback?.resolutionNote && { resolutionNote: feedback.resolutionNote }),
      };

      await onSubmit(feedbackData);
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setErrors({ submit: 'Failed to submit feedback. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl border border-gray-200 max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-8 border-b border-gray-200">
          <h2 className="text-2xl font-display font-bold text-gray-900">
            {isEditing ? 'Edit Feedback' : 'Submit Feedback'}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-text font-semibold text-gray-700 mb-2">
              Title *
            </label>
            <Input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Brief description of the feedback"
              error={errors.title}
              className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400 font-text"
            />
          </div>

          {/* Category and Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="category" className="block text-sm font-text font-semibold text-gray-700 mb-2">
                Category
              </label>
              <Select
                id="category"
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                options={categoryOptions}
                className="w-full"
              />
            </div>
            <div>
              <label htmlFor="priority" className="block text-sm font-text font-semibold text-gray-700 mb-2">
                Priority
              </label>
              <Select
                id="priority"
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                options={priorityOptions}
                className="w-full"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-text font-semibold text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Please provide detailed information about your feedback..."
              rows={6}
              className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400 resize-none font-text"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="p-4 bg-red-50/50 backdrop-blur-sm border border-red-200 rounded-2xl">
              <p className="text-sm text-red-600 font-text">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-6 py-2.5 bg-white/40 backdrop-blur-xl border-gray-200"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="min-w-[120px] px-6 py-2.5 shadow-lg shadow-primary-400/25 hover:shadow-xl hover:shadow-primary-400/30"
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Saving...</span>
                </div>
              ) : isEditing ? (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Submit Feedback
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
